// src/components/raports/JurnalTvaPrint.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Card, Spinner, Alert, Table, Button } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const fmt2 = (n) =>
  Number(n ?? 0).toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function JurnalTvaPrint() {
  const [sp] = useSearchParams();
  const from = sp.get("from");
  const to = sp.get("to");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ rows: [], totals_by_cota: [] });

  useEffect(() => {
    if (!from || !to) return;

    (async () => {
      setBusy(true);
      setErr("");
      try {
        // IMPORTANT: asta ajunge la http://localhost:8000/rapoarte/jurnal-tva?... prin authFetch
        const res = await authFetch(
          `/rapoarte/jurnal-tva?from=${from}&to=${to}`,
        );
        setData({
          rows: Array.isArray(res?.rows) ? res.rows : [],
          totals_by_cota: Array.isArray(res?.totals_by_cota)
            ? res.totals_by_cota
            : [],
        });
      } catch (e) {
        setErr(e?.message || "Eroare la încărcare jurnal TVA.");
      } finally {
        setBusy(false);
      }
    })();
  }, [from, to]);

  const groupsByCota = useMemo(() => {
    const m = new Map(); // key: cota (number), value: rows[]
    for (const r of data.rows || []) {
      const k = Number(r.cota ?? 0);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }

    // sortare documente în fiecare grup
    for (const rows of m.values()) {
      rows.sort((a, b) => {
        const da = String(a.doc_date || "");
        const db = String(b.doc_date || "");
        if (da !== db) return da.localeCompare(db);
        return String(a.doc_no || "").localeCompare(String(b.doc_no || ""));
      });
    }

    // sortare grupuri după cotă
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]); // [[cota, rows], ...]
  }, [data.rows]);

  if (!from || !to)
    return <Alert variant="warning">Lipsesc parametrii from/to în URL.</Alert>;
  if (busy)
    return (
      <div className="p-3">
        <Spinner animation="border" />
      </div>
    );
  if (err)
    return (
      <div className="p-3">
        <Alert variant="danger">{err}</Alert>
      </div>
    );

  // grupare pe doc_id
  const grouped = new Map();
  for (const r of data.rows) {
    if (!grouped.has(r.doc_id)) grouped.set(r.doc_id, []);
    grouped.get(r.doc_id).push(r);
  }

  return (
    <div className="p-3">
      <div className="d-print-none mb-2">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => window.print()}
        >
          Tipărește / PDF
        </Button>
      </div>
      <Card className="shadow-sm">
        <Card.Body>
          <div class="p-3 jurnal-tva-print">
            <div className="d-flex justify-content-between align-items-baseline">
              <h5 className="mb-0">Jurnal TVA</h5>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Perioada: {from} → {to}
              </div>
            </div>

            <div className="mt-3">
              <Table bordered={false} size="sm" className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: 85 }}>Data</th>
                    <th style={{ width: 70 }}>Nr</th>
                    <th>Denumire</th>
                    <th style={{ width: 120 }}>Cod fiscal</th>
                    <th style={{ width: 110 }} className="text-end">
                      Bază
                    </th>
                    <th style={{ width: 110 }} className="text-end">
                      TVA
                    </th>
                    <th style={{ width: 110 }} className="text-end">
                      Total
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {groupsByCota.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        Nu există date.
                      </td>
                    </tr>
                  ) : (
                    groupsByCota.map(([cota, rows]) => {
                      const tot = (data.totals_by_cota || []).find(
                        (x) => Number(x.cota) === cota,
                      );
                      const gb =
                        tot?.baza ??
                        rows.reduce((s, r) => s + Number(r.baza || 0), 0);
                      const gt =
                        tot?.tva ??
                        rows.reduce((s, r) => s + Number(r.tva || 0), 0);
                      const gg =
                        tot?.total ??
                        rows.reduce((s, r) => s + Number(r.total || 0), 0);

                      return (
                        <React.Fragment key={`cota-${cota}`}>
                          {/* HEADER GRUP COTA */}
                          <tr className="row-group">
                            <td colSpan={4} className="fw-bold">
                              Cota TVA {cota}%
                            </td>
                            <td className="text-end fw-bold">{fmt2(gb)}</td>
                            <td className="text-end fw-bold">{fmt2(gt)}</td>
                            <td className="text-end fw-bold">{fmt2(gg)}</td>
                          </tr>

                          {/* DOCUMENTE DIN GRUP */}
                          {rows.map((r, idx) => (
                            <tr key={`cota-${cota}-${r.doc_id}-${idx}`}>
                              <td>{r.doc_date}</td>
                              <td>{r.doc_no}</td>
                              <td>{r.partener}</td>
                              <td>{r.cui}</td>
                              <td className="text-end">{fmt2(r.baza)}</td>
                              <td className="text-end">{fmt2(r.tva)}</td>
                              <td className="text-end">{fmt2(r.total)}</td>
                            </tr>
                          ))}

                          {/* linie de separare între grupuri */}
                          <tr className="row-gap">
                            <td colSpan={7} />
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>

                <tfoot>
                  {(() => {
                    const t = data.totals_by_cota || [];
                    const gb = t.reduce((s, x) => s + Number(x.baza || 0), 0);
                    const gt = t.reduce((s, x) => s + Number(x.tva || 0), 0);
                    const gg = t.reduce((s, x) => s + Number(x.total || 0), 0);

                    return (
                      <tr className="row-total-general">
                        <td colSpan={4} className="text-end">
                          TOTAL GENERAL
                        </td>
                        <td className="text-end">{fmt2(gb)}</td>
                        <td className="text-end">{fmt2(gt)}</td>
                        <td className="text-end">{fmt2(gg)}</td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </Table>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
